import * as React from 'react';
import {StyleSheet, View, Switch} from 'react-native';
import TextInput from '../../elements/AVTextInput';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import BackSubheader from '../../elements/BackSubheader';
import CreditCardInput from '../../elements/funding/CreditCardInput';
import KeyboardAwareScrollView from '../../elements/KeyboardAwareScrollView';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import withIsConnected from '../../hoc/withIsConnected';
import Styles from '../../Styles';
import type {ScreenProps, IsConnectedProps} from 'src/types/Screen';
import ActionsFactory from 'src/actions/ActionsFactory';
import PersistentStore from 'src/services/PersistentStoreService';
import Localized from 'src/constants/AppStrings';
import {alertError, confirm} from '../../helpers/AlertHelper';
import CreditCard from 'src/models/CreditCard';
import AVText from 'src/components/elements/AVText';
import Star from 'src/components/img/svg/inbox/Star';
import Events from 'src/logging/Events';
type CreditCardDetailScreenProps = IsConnectedProps & {
  creditCard: CreditCard;
};
type CreditCardDetailScreenState = {
  defaultTokenStatus: boolean;
};

class CreditCardDetailScreen extends React.Component<
  CreditCardDetailScreenProps,
  CreditCardDetailScreenState
> {
  scrollView: KeyboardAwareScrollView | null;
  static contextType = ScreenContext;

  constructor(props: CreditCardDetailScreenProps) {
    super(props);
    this.state = {
      defaultTokenStatus: this.props.creditCard.isDefault,
    };
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleSetAsDefaultClick = this.handleSetAsDefaultClick.bind(this);
  }

  async handleDeleteClick() {
    confirm(Localized.Labels.confirm_delete_card, async () => {
      this.context.actions.showSpinner();
      const accountId = await PersistentStore.getAccountId();
      const response = await ActionsFactory.getAccountActions().deleteCreditCard(
        accountId || '',
        this.props.creditCard.id || '',
        this.props.creditCard.accountBalanceId || '',
        this.props.creditCard.isDefault,
      );
      this.context.actions.hideSpinner();
      if (response?.status === 'ok') {
        NavActions.pop();
      } else {
        alertError(Localized.Errors.error_updating_credit_card);
      }
    });
  }

  async handleSetAsDefaultClick() {
    confirm(
      Localized.Labels.confirm_set_as_default_card,
      async () => {
        this.context.actions.showSpinner();
        try {
          const accountId = await PersistentStore.getAccountId();
          await ActionsFactory.getAccountActions().updateDefaultToken(
            accountId || '',
            this.props.creditCard.accountBalanceId || '',
            this.props.creditCard.id || '',
          );
          this.context.actions.hideSpinner();
          NavActions.pop();
        } catch (error) {
          Events.Error.trackEvent(
            'Exception',
            'CreditCardDetailScreen:HandleSetAsDefaultClick',
            error.message ? error.message : error.toString(),
          );
          alertError(Localized.Errors.error_setting_default_credit_card);
        }
      },
      () => NavActions.pop(),
    );
  }

  renderCardDefaultStatus() {
    if (this.props.creditCard.isDefault) {
      return (
        <View style={styles.defaultStatusView}>
          <Star width={Styles.Fonts.f4} height={Styles.Fonts.f4} />
          <AVText style={styles.defaultLabel}>
            {Localized.Labels.this_card_is_set_as_your_default}
          </AVText>
        </View>
      );
    } else {
      return (
        <View style={styles.defaultStatusView}>
          <AVText style={styles.setDefaultLabel}>
            {Localized.Labels.set_as_default}
          </AVText>
          <Switch
            style={styles.defaultSwitch}
            accessibilityLabel="Set as Default"
            nativeID="Set as Default"
            value={this.state.defaultTokenStatus}
            onValueChange={(value) => {
              this.setState({defaultTokenStatus: value});
              this.handleSetAsDefaultClick();
            }}
            trackColor={{
              false: Styles.auxColor,
              true: Styles.primaryColor,
            }}
          />
        </View>
      );
    }
  }

  render() {
    return (
      <BackSubheader title={Localized.Labels.credit_card}>
        <View style={styles.content}>
          <KeyboardAwareScrollView
            contentContainerStyle={styles.scrollView}
            keyboardDismissMode="interactive"
            style={styles.scrollView}
            ref={(scrollView) => {
              this.scrollView = scrollView;
            }}
            automaticallyAdjustContentInsets={false}
          >
            <View style={styles.inputContainer}>
              <CreditCardInput
                accessibilityLabel="Card Number"
                label={Localized.Labels.card_number}
                editable={false}
                issuer={this.props.creditCard.cardIssuer}
                value={this.props.creditCard.name}
              />
              <View style={styles.row}>
                <View style={styles.inputRowLeft}>
                  <TextInput
                    accessibilityLabel="Exp Month"
                    editable={false}
                    value={this.props.creditCard.expirationMonth.toString()}
                    label={Localized.Labels.expiration_month}
                    style={Styles.Style.font500}
                  />
                </View>
                <View style={styles.inputRowRight}>
                  <TextInput
                    accessibilityLabel="Exp Year"
                    editable={false}
                    value={this.props.creditCard.expirationYear.toString()}
                    label={Localized.Labels.expiration_year}
                    style={Styles.Style.font500}
                  />
                </View>
              </View>
              <View style={styles.row}>
                <View style={styles.inputRowLeft}>
                  <TextInput
                    accessibilityLabel="Card Type"
                    editable={false}
                    value={this.props.creditCard.cardIssuer}
                    label={Localized.Labels.card_type}
                    style={Styles.Style.font500}
                  />
                </View>
              </View>
              {this.renderCardDefaultStatus()}
            </View>
          </KeyboardAwareScrollView>

          {this.props.isConnected && (
            <RoundedButton
              buttonType={ButtonType.action}
              color={Styles.dangerColor}
              accessibilityLabel="Delete Card"
              onPress={this.handleDeleteClick}
              text={Localized.Buttons.delete_card}
            />
          )}
        </View>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  inputContainer: {
    flexDirection: 'column',
    marginHorizontal: Styles.Spacing.m3,
    paddingTop: Styles.Spacing.m1,
  },
  inputRowLeft: {
    flex: 0.5,
    paddingRight: Styles.Spacing.m1,
  },
  inputRowRight: {
    flex: 0.5,
    paddingLeft: Styles.Spacing.m1,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  defaultStatusView: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: Styles.Spacing.m1,
    marginTop: Styles.Spacing.m2,
  },
  defaultSwitch: {
    transform: [
      {
        scaleX: 0.85,
      },
      {
        scaleY: 0.85,
      },
    ],
  },
  defaultLabel: {
    fontSize: Styles.Fonts.f0,
    marginLeft: Styles.Spacing.m2,
    marginRight: Styles.Spacing.m1,
    fontStyle: 'italic',
  },
  setDefaultLabel: {
    fontSize: Styles.Fonts.f1,
  },
  scrollView: {
    alignSelf: 'stretch',
  },
});
export default withForwardedNavigationParams()(
  withIsConnected(CreditCardDetailScreen),
);
